// "Kérdezz az életedről" (roadmap 3.6): ask in plain Hungarian; the answer comes with its sources, and can be
// shown on the life graph. A conversation keeps going (follow-up questions); earlier ones can be reopened.
import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { api } from '../api';
import { day, Label, Panel } from '../ui';
import { JarvisSpinner } from '../Busy';

interface Card { id: string; occurred_at: string; kind: string; text: string; thread: { id: string; name: string } | null }
interface Turn { id: string; question: string; answer: string; sources: Card[]; usd?: number }
const KIND: Record<string, string> = { payment: 'fizetés', message: 'üzenet', task: 'teendő', exam: 'vizsga', class: 'óra', note: 'jegyzet', contact: 'névjegy' };
const EXAMPLES = ['Mennyibe került a linzi út, és ki mennyit fizetett vissza?', 'Mire ment el a legtöbb pénz októberben?', 'Mi a következő teendőm az egyetemen?', 'Kivel beszéltem utoljára a Második munkáról?'];

/** A tiny, safe markdown: **bold**, lists ("- "), paragraphs. No HTML from the model is ever rendered. */
function Md({ text }: { text: string }) {
  const inline = (s: string, k: number): ReactNode[] => s.split(/(\*\*[^*]+\*\*)/g).map((p, i) => (p.startsWith('**') && p.endsWith('**') ? <b key={`${k}-${i}`}>{p.slice(2, -2)}</b> : <Fragment key={`${k}-${i}`}>{p}</Fragment>));
  const blocks: ReactNode[] = [];
  let list: string[] = [];
  const flush = () => { if (list.length) { blocks.push(<ul key={blocks.length}>{list.map((l, i) => <li key={i}>{inline(l, i)}</li>)}</ul>); list = []; } };
  for (const line of text.split('\n')) {
    const m = /^\s*[-•*]\s+(.*)$/.exec(line);
    if (m) { list.push(m[1]!); continue; }
    flush();
    if (line.trim()) blocks.push(<p key={blocks.length}>{inline(line, blocks.length)}</p>);
  }
  flush();
  return <div className="md">{blocks}</div>;
}

export function Ask({ compact = false }: { compact?: boolean }) {
  const [conv, setConv] = useState<string | null>(() => sessionStorage.getItem('askConv'));
  const [turns, setTurns] = useState<Turn[]>([]);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [secs, setSecs] = useState(0);
  const [err, setErr] = useState('');
  const [list, setList] = useState<Array<{ id: string; title: string; updated_at: string; turns: number }> | null>(null);
  const end = useRef<HTMLDivElement>(null);

  useEffect(() => { if (conv) api<{ turns: Turn[] }>(`/api/ask/conversations/${conv}`).then((c) => setTurns(c.turns)).catch(() => setConv(null)); }, []);
  useEffect(() => { end.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [turns.length, busy]);
  useEffect(() => { if (!busy) return; setSecs(0); const t = setInterval(() => setSecs((s) => s + 1), 1000); return () => clearInterval(t); }, [busy]);

  const send = async (question: string) => {
    if (!question.trim() || busy) return;
    setErr(''); setBusy(true); setQ('');
    setTurns((t) => [...t, { id: 'pending', question, answer: '', sources: [] }]);
    try {
      const r = await api<{ conversation: string; turn: Turn }>('/api/ask', { question, conversation: conv }, { quiet: true });
      setConv(r.conversation); sessionStorage.setItem('askConv', r.conversation);
      setTurns((t) => [...t.filter((x) => x.id !== 'pending'), r.turn]);
    } catch (e: any) {
      setErr(e.message); setTurns((t) => t.filter((x) => x.id !== 'pending')); setQ(question);
    } finally { setBusy(false); }
  };
  const fresh = () => { setConv(null); sessionStorage.removeItem('askConv'); setTurns([]); setList(null); };
  const open = async (id: string) => { const c = await api<{ turns: Turn[] }>(`/api/ask/conversations/${id}`); setConv(id); sessionStorage.setItem('askConv', id); setTurns(c.turns); setList(null); };

  return (
    <div className={`j-stack ask ${compact ? 'compact' : ''}`}>
      <div className="row-split"><Label c="amber">Kérdezz az életedről</Label>
        <span className="j-row"><button className="j-btn small-btn" onClick={fresh}>Új</button>
          <button className="j-btn small-btn" onClick={() => (list ? setList(null) : api('/api/ask/conversations').then(setList))}>Korábbiak</button></span></div>
      {list && (
        <Panel c="amber">{list.length ? list.map((c) => <button key={c.id} className="conv" onClick={() => open(c.id)}><span>{c.title}</span><em>{day(c.updated_at)} · {c.turns}</em></button>) : <p className="muted">Még nincs korábbi beszélgetés.</p>}</Panel>
      )}
      {!turns.length && !list && (
        <div className="examples">{EXAMPLES.map((e) => <button key={e} className="j-btn example" onClick={() => send(e)}>{e}</button>)}</div>
      )}
      {turns.map((t) => (
        <div key={t.id} className="turn">
          <div className="q">{t.question}</div>
          {t.id === 'pending' ? (
            <Panel c="amber" className="a"><div className="thinking"><JarvisSpinner size={34} /><span>{secs < 4 ? 'Jarvis gondolkodik…' : secs < 12 ? 'Keres az adataidban…' : 'Összerakja a választ…'} <em className="j-num">{secs} mp</em></span></div></Panel>
          ) : (
            <Panel c="amber" className="a">
              <Md text={t.answer} />
              <div className="j-row a-acts">
                {t.sources.length > 0 && <a className="j-btn small-btn" href={`#/graf?ask=${t.id}`}>Mutasd a gráfon</a>}
                {t.sources.length > 0 && <details className="sources"><summary>{t.sources.length} forrás</summary>
                  {t.sources.map((s) => (
                    <a key={s.id} className="src-card" href={s.thread ? `#/szal/${s.thread.id}` : undefined}>
                      <span className="when">{day(s.occurred_at)} · {KIND[s.kind] ?? s.kind}{s.thread ? ` · ${s.thread.name}` : ''}</span><span>{s.text}</span>
                    </a>
                  ))}</details>}
              </div>
            </Panel>
          )}
        </div>
      ))}
      {err && <p className="err">{err}</p>}
      <form className="ask-form" onSubmit={(e) => { e.preventDefault(); send(q); }}>
        <textarea className="j-input" rows={2} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kérdezz bármit az életedről…"
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(q); } }} />
        <button className="j-btn j-btn--primary" disabled={busy || !q.trim()}>Kérdezem</button>
      </form>
      <div ref={end} />
    </div>
  );
}
